// ponytail: stand-in for <menu-drawer>/<header-drawer> from Shrine Pro's secondary.js, which this layout
// doesn't load (and shouldn't: it wipes document.body in one slider path). Ported as-is, except focus
// handling just moves focus instead of fully trapping it (trapFocus lived in the missing global.js).
(() => {
  const trapFocus = (container, elementToFocus = container) => elementToFocus?.focus?.();
  const removeTrapFocus = (elementToFocus) => elementToFocus?.focus?.();

  class MenuDrawer extends HTMLElement {
    constructor() {
      super();
      this.mainDetailsToggle = this.querySelector('details');
      this.addEventListener('keyup', this.onKeyUp.bind(this));
      this.addEventListener('focusout', this.onFocusOut.bind(this));
      this.bindEvents();
    }

    bindEvents() {
      this.querySelectorAll('summary').forEach((summary) => summary.addEventListener('click', this.onSummaryClick.bind(this)));
      this.querySelectorAll('button:not(.menu-drawer__close-menu-btn)').forEach((button) =>
        button.addEventListener('click', this.onCloseButtonClick.bind(this))
      );
    }

    onKeyUp(event) {
      if (event.code?.toUpperCase() !== 'ESCAPE') return;
      const openDetails = event.target.closest('details[open]');
      if (!openDetails) return;
      openDetails === this.mainDetailsToggle
        ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
        : this.closeSubmenu(openDetails);
    }

    onSummaryClick(event) {
      const summaryElement = event.currentTarget;
      const detailsElement = summaryElement.parentNode;
      const parentMenuElement = detailsElement.closest('.has-submenu');
      const isOpen = detailsElement.hasAttribute('open');

      if (detailsElement === this.mainDetailsToggle) {
        if (isOpen) event.preventDefault();
        isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      } else {
        setTimeout(() => {
          detailsElement.classList.add('menu-opening');
          summaryElement.setAttribute('aria-expanded', true);
          parentMenuElement?.classList.add('submenu-open');
          trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
        }, 100);
      }
    }

    openMenuDrawer(summaryElement) {
      setTimeout(() => this.mainDetailsToggle.classList.add('menu-opening'));
      summaryElement.setAttribute('aria-expanded', true);
      trapFocus(this.mainDetailsToggle, summaryElement);
      document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
    }

    closeMenuDrawer(event, elementToFocus = false) {
      if (event === undefined) return;
      this.mainDetailsToggle.classList.remove('menu-opening');
      this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
        details.removeAttribute('open');
        details.classList.remove('menu-opening');
      });
      this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => submenu.classList.remove('submenu-open'));
      document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
      removeTrapFocus(elementToFocus);
      this.closeAnimation(this.mainDetailsToggle);
    }

    onFocusOut() {
      setTimeout(() => {
        if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement)) this.closeMenuDrawer();
      });
    }

    onCloseButtonClick(event) {
      this.closeSubmenu(event.currentTarget.closest('details'));
    }

    closeSubmenu(detailsElement) {
      if (!detailsElement) return;
      detailsElement.closest('.submenu-open')?.classList.remove('submenu-open');
      detailsElement.classList.remove('menu-opening');
      detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
      removeTrapFocus(detailsElement.querySelector('summary'));
      this.closeAnimation(detailsElement);
    }

    closeAnimation(detailsElement) {
      let animationStart;
      const handleAnimation = (time) => {
        if (animationStart === undefined) animationStart = time;
        if (time - animationStart < 400) return window.requestAnimationFrame(handleAnimation);
        detailsElement.removeAttribute('open');
      };
      window.requestAnimationFrame(handleAnimation);
    }
  }

  class HeaderDrawer extends MenuDrawer {
    constructor() {
      super();
      this.querySelectorAll('.menu-drawer__close-menu-btn').forEach((button) => button.addEventListener('click', this.closeButtonClick.bind(this)));
    }

    openMenuDrawer(summaryElement) {
      this.header = this.header || document.querySelector('.section-header');
      this.borderOffset = this.closest('.header-wrapper')?.classList.contains('header-wrapper--border-bottom') ? 1 : 0;
      if (this.header) {
        document.documentElement.style.setProperty(
          '--header-bottom-position',
          `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
        );
        this.header.classList.add('menu-open');
      }
      super.openMenuDrawer(summaryElement);
    }

    closeMenuDrawer(event, elementToFocus) {
      super.closeMenuDrawer(event, elementToFocus);
      if (event !== undefined) this.header?.classList.remove('menu-open');
    }

    closeButtonClick(event) {
      this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'));
      this.querySelector('.header__icon--menu[aria-expanded=true]')?.setAttribute('aria-expanded', 'false');
    }
  }

  if (!customElements.get('menu-drawer')) customElements.define('menu-drawer', MenuDrawer);
  if (!customElements.get('header-drawer')) customElements.define('header-drawer', HeaderDrawer);
})();
